import { useMemo } from 'react'

import {
  IConditionalField,
  IPermitTypeField,
} from '~/client/graph/types.generated'
import {
  materialPermitFieldTypes,
  materialResourcePermitFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { FORM_MATERIAL_RESOURCE_ASSOCIATED_FIELDS_KEY } from '~/client/src/shared/constants/permitTypeFieldsConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import { parseConditionalKeyIds } from '~/client/src/shared/utils/conditionalKeyUtils'

import { IConditionalSelectOption } from '../types/IConditionalSelectOption'
import {
  flattenRuleKeys,
  getAllRulesForField,
  partitionRules,
} from '../utils/ruleHelpers'

export function useRuleManagement(
  field: IPermitTypeField,
  isLocationFieldType: boolean,
  allConditionalFields: IConditionalField[],
  selectOptions: IConditionalSelectOption[],
  locationAttributesStore: LocationAttributesStore,
  isMaterialResourceField?: boolean,
  materialResourceRecords?: IResourceRecord[],
) {
  const allRules = useMemo(() => {
    if (!field) return []
    return getAllRulesForField(allConditionalFields, field.id).filter(
      r => r.key !== FORM_MATERIAL_RESOURCE_ASSOCIATED_FIELDS_KEY,
    )
  }, [allConditionalFields, allConditionalFields.length, field?.id])

  const allUsedLocationIds = useMemo(
    () => flattenRuleKeys(allRules.map(r => r.key)),
    [allRules],
  )

  const { availableRules, inactiveRules } = useMemo(() => {
    if (
      materialPermitFieldTypes.includes(field.type) ||
      materialResourcePermitFieldTypes.includes(field.type)
    ) {
      return {
        availableRules: allRules,
        inactiveRules: [] as IConditionalField[],
      }
    }
    const { available, inactive } = partitionRules(allRules, selectOptions)
    return { availableRules: available, inactiveRules: inactive }
  }, [allRules, selectOptions, field.type])

  const getRuleDisplayName = useMemo(
    () =>
      (key: string): string => {
        if (isLocationFieldType) {
          const ids = parseConditionalKeyIds(key)
          return (
            locationAttributesStore.getById(ids[0])?.name ||
            Localization.translator.unknownDeletedObject
          )
        }
        if (isMaterialResourceField && materialResourceRecords?.length) {
          const ids = parseConditionalKeyIds(key)
          const record = materialResourceRecords.find(r => r.id === ids[0])
          return record?.name || Localization.translator.unknownDeletedObject
        }
        const option = selectOptions?.find(opt => opt.value === key)
        return option?.title || key
      },
    [
      isLocationFieldType,
      isMaterialResourceField,
      materialResourceRecords,
      locationAttributesStore,
      selectOptions,
      Localization.state.currentLanguage,
    ],
  )

  return useMemo(
    () => ({
      allRules,
      allUsedLocationIds,
      availableRules,
      inactiveRules,
      getRuleDisplayName,
    }),
    [
      allRules,
      allUsedLocationIds,
      availableRules,
      inactiveRules,
      getRuleDisplayName,
    ],
  )
}
